<template>
  <el-dialog
    title="工作区默认权限白名单"
    :visible.sync="visible"
    width="720px"
    append-to-body
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <el-alert
      type="info"
      :closable="false"
      show-icon
      style="margin-bottom: 16px;"
      title="白名单内人员对「项目申报 / 客户审核资料」根目录及其全部子孙（含未来新建）自动拥有全部控制权。"
    />

    <el-tabs v-model="activeTab" type="card">
      <el-tab-pane label="人员" name="user">
        <select-member-modal
          placeholder="请选择白名单人员"
          v-model="memberPickerValue"
          :trigger-change="true"
          value-field="id"
          :multiple="true"
          :popover-z-index="5000"
          @send-change="handleMemberSendChange"
        />
      </el-tab-pane>
      <el-tab-pane label="部门" name="dept">
        <el-tree
          v-loading="groupLoading"
          :data="deptTreeList"
          show-checkbox
          node-key="deptId"
          ref="deptTree"
          :props="{ children: 'children', label: 'deptName' }"
          @check="syncDeptSelection"
        />
      </el-tab-pane>
      <el-tab-pane label="角色" name="role">
        <div v-loading="groupLoading" class="role-list">
          <el-checkbox-group v-model="selectedRoleIds" @change="syncRoleSelection">
            <el-checkbox
              v-for="role in roleList"
              :key="role.id"
              :label="Number(role.id)"
              style="display:block;margin:8px 0;"
            >{{ role.roleName }}</el-checkbox>
          </el-checkbox-group>
          <el-empty v-if="!roleList.length" description="暂无角色" :image-size="60" />
        </div>
      </el-tab-pane>
    </el-tabs>

    <div class="selected-panel">
      <div class="selected-title">已选白名单（{{ selectedItems.length }}）</div>
      <div>
        <el-tag
          v-for="item in selectedItems"
          :key="item.subjectType + '-' + item.subjectId"
          closable
          size="small"
          style="margin: 4px;"
          @close="removeItem(item)"
        >
          {{ typeLabel(item.subjectType) }} · {{ item.name || item.subjectId }}
        </el-tag>
        <span v-if="!selectedItems.length" style="color:#909399;">暂未选择</span>
      </div>
    </div>

    <span slot="footer">
      <el-button @click="visible = false">取 消</el-button>
      <el-button type="primary" :loading="saving" @click="handleSave">保 存</el-button>
    </span>
  </el-dialog>
</template>

<script>
export default {
  name: 'WorkspaceWhitelistDialog',
  components: {
    SelectMemberModal: () => import('@/components/SelectMemberModal.vue')
  },
  data() {
    return {
      visible: false,
      saving: false,
      groupLoading: false,
      activeTab: 'user',
      memberPickerValue: '',
      selectedItems: [],
      userList: [],
      deptTreeList: [],
      roleList: [],
      selectedRoleIds: [],
      syncing: false
    }
  },
  methods: {
    typeLabel(t) {
      return { 0: '人员', 1: '部门', 2: '角色' }[t] || '未知'
    },
    open() {
      this.visible = true
      this.loadGroup().then(() => this.loadWhitelist())
    },
    handleClose() {
      this.memberPickerValue = ''
    },
    findDeptName(deptId, list = this.deptTreeList) {
      if (!list || !list.length) return ''
      for (const node of list) {
        if (Number(node.deptId) === Number(deptId)) {
          return node.deptName || ''
        }
        const child = this.findDeptName(deptId, node.children || [])
        if (child) return child
      }
      return ''
    },
    resolveItemName(subjectType, subjectId, fallback) {
      if (subjectType === 0) {
        const user = (this.userList || []).find(u => Number(u.id) === Number(subjectId))
        if (user) return user.fullName || user.username || user.name || String(subjectId)
      } else if (subjectType === 1) {
        const name = this.findDeptName(subjectId)
        if (name) return name
      } else if (subjectType === 2) {
        const role = (this.roleList || []).find(r => Number(r.id) === Number(subjectId))
        if (role) return role.roleName || String(subjectId)
      }
      return fallback || String(subjectId)
    },
    enrichSelectedNames() {
      this.selectedItems = (this.selectedItems || []).map(item => ({
        ...item,
        name: this.resolveItemName(item.subjectType, item.subjectId, item.name)
      }))
    },
    loadGroup() {
      this.groupLoading = true
      return this.$HttpService.post('/permission/getPermissionGroup')
        .then(res => {
          this.userList = (res && res.userList) || []
          this.deptTreeList = (res && res.deptTreeList) || []
          this.roleList = (res && res.roleList) || []
        })
        .catch(() => {
          this.$message.error('获取组织数据失败')
        })
        .finally(() => {
          this.groupLoading = false
        })
    },
    loadWhitelist() {
      return this.$HttpService.get('/workspace-whitelist/list').then(list => {
        const rows = Array.isArray(list) ? list : []
        this.selectedItems = rows.map(r => ({
          subjectType: r.subjectType,
          subjectId: Number(r.subjectId),
          name: this.resolveItemName(r.subjectType, r.subjectId, r.name)
        }))
        this.enrichSelectedNames()
        this.syncing = true
        const users = this.selectedItems.filter(i => i.subjectType === 0)
        this.memberPickerValue = users.map(u => u.subjectId).join(',')
        this.selectedRoleIds = this.selectedItems
          .filter(i => i.subjectType === 2)
          .map(i => Number(i.subjectId))
        this.$nextTick(() => {
          if (this.$refs.deptTree) {
            const keys = this.selectedItems
              .filter(i => i.subjectType === 1)
              .map(i => i.subjectId)
            this.$refs.deptTree.setCheckedKeys(keys)
          }
          this.syncing = false
          // 人员选择器回填后可能异步触发 send-change，再补一次名称
          this.$nextTick(() => this.enrichSelectedNames())
        })
      }).catch(() => {
        this.$message.error('获取白名单失败，请确认已执行建表 SQL')
      })
    },
    upsertItem(item) {
      const withName = {
        ...item,
        name: this.resolveItemName(item.subjectType, item.subjectId, item.name)
      }
      const idx = this.selectedItems.findIndex(
        i => i.subjectType === withName.subjectType && Number(i.subjectId) === Number(withName.subjectId)
      )
      if (idx >= 0) {
        this.$set(this.selectedItems, idx, withName)
      } else {
        this.selectedItems.push(withName)
      }
    },
    removeItem(item) {
      this.selectedItems = this.selectedItems.filter(
        i => !(i.subjectType === item.subjectType && Number(i.subjectId) === Number(item.subjectId))
      )
      this.syncing = true
      if (item.subjectType === 0) {
        this.memberPickerValue = this.selectedItems
          .filter(i => i.subjectType === 0)
          .map(i => i.subjectId)
          .join(',')
      }
      if (item.subjectType === 1 && this.$refs.deptTree) {
        this.$refs.deptTree.setCheckedKeys(
          this.selectedItems.filter(i => i.subjectType === 1).map(i => i.subjectId)
        )
      }
      if (item.subjectType === 2) {
        this.selectedRoleIds = this.selectedItems
          .filter(i => i.subjectType === 2)
          .map(i => Number(i.subjectId))
      }
      this.$nextTick(() => { this.syncing = false })
    },
    handleMemberSendChange(list) {
      if (this.syncing) return
      const nextUsers = []
      ;(list || []).forEach(u => {
        const id = u.id != null && u.id !== '' ? u.id : u.username
        if (id === undefined || id === null || id === '') return
        nextUsers.push({
          subjectType: 0,
          subjectId: Number(id),
          name: u.fullName || u.username || this.resolveItemName(0, id, String(id))
        })
      })
      // 回填阶段选择器可能先抛出无姓名数据，避免用空列表覆盖已解析的白名单
      if (!nextUsers.length && this.selectedItems.some(i => i.subjectType === 0)) {
        return
      }
      this.selectedItems = this.selectedItems.filter(i => i.subjectType !== 0)
      nextUsers.forEach(item => this.upsertItem(item))
      this.enrichSelectedNames()
    },
    syncDeptSelection() {
      if (this.syncing || !this.$refs.deptTree) return
      const nodes = this.$refs.deptTree.getCheckedNodes(false, true) || []
      this.selectedItems = this.selectedItems.filter(i => i.subjectType !== 1)
      nodes.forEach(n => {
        this.upsertItem({
          subjectType: 1,
          subjectId: Number(n.deptId),
          name: n.deptName
        })
      })
    },
    syncRoleSelection(val) {
      if (this.syncing) return
      this.selectedItems = this.selectedItems.filter(i => i.subjectType !== 2)
      ;(val || []).forEach(id => {
        const role = this.roleList.find(r => Number(r.id) === Number(id))
        this.upsertItem({
          subjectType: 2,
          subjectId: Number(id),
          name: role ? role.roleName : String(id)
        })
      })
    },
    handleSave() {
      this.saving = true
      const members = this.selectedItems.map(i => ({
        subjectType: i.subjectType,
        subjectId: Number(i.subjectId)
      }))
      this.$HttpService.post('/workspace-whitelist/save', { members })
        .then(() => {
          this.$message.success('白名单已保存')
          this.visible = false
          this.$emit('ok')
        })
        .catch(() => {
          this.$message.error('保存失败')
        })
        .finally(() => {
          this.saving = false
        })
    }
  }
}
</script>

<style scoped>
.selected-panel {
  margin-top: 16px;
  padding: 12px;
  background: #f5f7fa;
  border-radius: 4px;
}
.selected-title {
  font-weight: 600;
  margin-bottom: 8px;
}
.role-list {
  max-height: 280px;
  overflow: auto;
  padding: 4px 8px;
}
</style>
